import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {UserModel} from '../models/UserModel'
import {CompanyData, SelectedCompany} from '../helpers/CompanyStorage'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  SetCompanies: '[Set Companies] Action',
  SetSelectedCompany: '[Set Selected Company] Action',
  ClearSelectedCompany: '[Clear Selected Company] Action',
  SetGlobalRoles: '[Set Global Roles] Action',
  SetHasGlobalRole: '[Set Has Global Role] Action',
}

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
  companies: [],
  selectedCompany: undefined,
  globalRoles: [],
  hasGlobalRole: false,
}

export interface IAuthState {
  user?: UserModel
  accessToken?: string
  companies: CompanyData[]
  selectedCompany?: SelectedCompany
  globalRoles: any[]
  hasGlobalRole: boolean
}

export const reducer = persistReducer(
  {storage, key: 'v100-xbox-auth', whitelist: ['user', 'accessToken', 'selectedCompany', 'globalRoles', 'companies', 'hasGlobalRole']},
  (state: IAuthState = initialAuthState, action: ActionWithPayload<any>) => {
    switch (action.type) {
      case actionTypes.Login: {
        const accessToken = action.payload?.accessToken
        return {accessToken, user: undefined, companies: [], selectedCompany: undefined, globalRoles: [], hasGlobalRole: false}
      }

      case actionTypes.Register: {
        const accessToken = action.payload?.accessToken
        return {accessToken, user: undefined, companies: [], selectedCompany: undefined, globalRoles: [], hasGlobalRole: false}
      }

      case actionTypes.Logout: {
        return {
          ...initialAuthState,
          companies: [],
          selectedCompany: undefined,
          globalRoles: [],
          hasGlobalRole: false,
        }
      }

      case actionTypes.UserRequested: {
        return {...state, user: undefined}
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user
        return {...state, user}
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user
        return {...state, user}
      }

      case actionTypes.SetCompanies: {
        const companies = action.payload?.companies || []
        
        return {...state, companies}
      }

      case actionTypes.SetSelectedCompany: {
        const selectedCompany = action.payload?.selectedCompany
        return {...state, selectedCompany}
      }

      case actionTypes.ClearSelectedCompany: {
        return {...state, selectedCompany: undefined}
      }

      case actionTypes.SetGlobalRoles: {
        const globalRoles = action.payload?.globalRoles
        return {...state, globalRoles}
      }

      case actionTypes.SetHasGlobalRole: {
        const hasGlobalRole = Boolean(action.payload?.hasGlobalRole)
        return {...state, hasGlobalRole}
      }

      default:
        return state
    }
  }
)

export const actions = {
  login: (accessToken: string) => ({type: actionTypes.Login, payload: {accessToken}}),
  register: (accessToken: string) => ({
    type: actionTypes.Register,
    payload: {accessToken},
  }),
  logout: () => ({type: actionTypes.Logout}),
  requestUser: () => ({
    type: actionTypes.UserRequested,
  }),
  fulfillUser: (user: UserModel) => ({type: actionTypes.UserLoaded, payload: {user}}),
  setUser: (user: UserModel) => ({type: actionTypes.SetUser, payload: {user}}),
  setCompanies: (companies: CompanyData[]) => ({type: actionTypes.SetCompanies, payload: {companies}}),
  setSelectedCompany: (selectedCompany: SelectedCompany) => ({type: actionTypes.SetSelectedCompany, payload: {selectedCompany}}),
  clearSelectedCompany: () => ({type: actionTypes.ClearSelectedCompany}),
  setGlobalRoles: (globalRoles: any[]) => ({type: actionTypes.SetGlobalRoles, payload: {globalRoles}}),
  setHasGlobalRole: (hasGlobalRole: boolean) => ({type: actionTypes.SetHasGlobalRole, payload: {hasGlobalRole}}),
}
